<template>
  <PopUp ref="popup">
    <!-- ------------ -->
    <!--  container   -->
    <!-- ------------ -->
    <div class="container-fluid p-3">
      <div class="col p-2">
        <!-- ---------------- -->
        <!--  row 1 - title   -->
        <!-- ---------------- -->
        <div class="row">
          <div class="bb-hdg mb-2 text-center bb-truncate-text">
            {{ title }}
          </div>
        </div>
        <!-- ------------------ -->
        <!--  row 2 - counter   -->
        <!-- ------------------ -->
        <div class="row mt-2 mb-3 justify-content-center">
          <!-- -------- -->
          <!-- counter  -->
          <!-- -------- -->
          <div class="col-auto text-center mb-2">
            <!-- <CounterComponent :pass_label="'Qty'" :pass_ref="'counter_ref'" :pass_count="value" :pass_min="0"
              :pass_max="99" :pass_digits="2" /> -->
            <CounterEmit
              ref="counter"
              :pass_ref="label"
              :pass_label="label"
              :pass_count="value"
              :pass_min="0"
              :pass_max="99"
              :pass_digits="2"
              @change="onCounterChanged"
            />
          </div>
        </div>
        <!-- ------------------ -->
        <!--  row 2 - buttons   -->
        <!-- ------------------ -->
        <div class="row mt-3 mb-2">
          <!-- ------------ -->
          <!--    buttons   -->
          <!-- ------------ -->
          <div
            class="btn-group special"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              type="submit"
              @click="_cancel"
              class="btn btn-lg btn-warning"
            >
              Cancel
            </button>
            <!-- ------------ -->
            <!--      OK      -->
            <!-- ------------ -->
            <button
              type="submit"
              @click="_confirm"
              class="btn btn-lg btn-primary"
            >
              OK
            </button>
          </div>
        </div>

        <!-- ------------------ -->
        <!--       errors       -->
        <!-- ------------------ -->
        <div
          v-if="errors.length > 0"
          class="row mt-2"
        >
          <div class="col text-center">
            <div
              v-for="err in errors"
              :key="err"
              class="bb-error-wrap"
              style="white-space: pre-line"
            >
              {{ err }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </PopUp>
</template>

<script>
import PopUp from './PopUp.vue'
import CounterEmit from './CounterEmit.vue'
import sleep from '../helpers/sleep'
import calcContainerAvailability from '../helpers/calcContainerAvailability'
import isEmpty from '../helpers/isEmpty'
import { inject } from 'vue'

export default {
  name: 'CounterDialog',
  components: {
    PopUp,
    CounterEmit,
  },
  //=====================================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //=====================================================================
  // data
  //------
  data: () => ({
    title: undefined,
    subject: undefined,
    row: undefined,
    character: undefined,
    label: '',
    value: 0,
    lastValidValue: 0,
    errors: [],
    resolvePromise: undefined,
    rejectPromise: undefined,
  }),
  //=====================================================================
  // methods
  //---------
  methods: {
    show(opts = {}) {
      // passed in parms
      this.title = opts.title
      this.label = opts.label
      this.row = opts.row
      this.character = opts.character

      const initial =
        this.row && this.row.tally_qty != null ? this.row.tally_qty : 0
      this.value = initial
      this.lastValidValue = initial
      this.errors = []

      console.log('CounterDialog show for row=', this.row)

      this.$refs.popup.open()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },
    //========================================================
    // on counter changed
    //--------------------
    onCounterChanged({ ref, count }) {
      console.log(
        'CounterDialog onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
      )

      const prevCount =
        this.lastValidValue != null
          ? this.lastValidValue
          : (this.row && this.row.tally_qty) || 0

      // where item is in a container, enforce container and parent
      // container capacity limits (mirrors ItemCard tally logic)
      if (count > 0 && this.row && this.row.container && this.character) {
        const availability = this.isContainerAvailabilityOnChange({
          count,
          prevCount,
        })
        if (!availability) {
          // revert to last valid value in the counter and model
          if (
            this.$refs.counter &&
            typeof this.$refs.counter.setCount === 'function'
          ) {
            this.$refs.counter.setCount(prevCount)
          }
          this.value = prevCount
          return
        }
      }

      // apply accepted value and clear any previous errors
      this.errors = []
      this.value = count
      this.lastValidValue = count
    },
    //========================================================
    // is container availability on change (dialog variant)
    //-----------------------------------------------------
    isContainerAvailabilityOnChange({ count, prevCount }) {
      let availability = true

      if (!this.row || !this.row.container) {
        return true
      }

      console.log(
        'CounterDialog: tally change with container id = ',
        this.row.container,
      )

      // get enc/capacity details for container
      const container = calcContainerAvailability({
        container_id: this.row.container,
        character: this.character,
      })

      // guard error
      if (isEmpty(container)) {
        console.error(
          'CounterDialog: unable to calculate container availability for container id ',
          this.row.container,
        )
      } else {
        const encPerItem = parseFloat(this.row.enc || 0)
        if (Number.isNaN(encPerItem)) {
          console.warn(
            'CounterDialog.isContainerAvailabilityOnChange: invalid encPerItem = ',
            this.row.enc,
          )
          return true
        }

        const capacity = container.capacity
        const contentsEnc = container.contents_enc

        console.log(
          'CounterDialog: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' prevCount = ',
          prevCount,
          ' new count = ',
          count,
        )

        // ENC this item currently occupies and will occupy after the change
        const prevEncForThisItemRaw = encPerItem * prevCount
        const prevEncForThisItem =
          Math.round((prevEncForThisItemRaw + Number.EPSILON) * 100) / 100

        const newTotalEncForThisItemRaw = encPerItem * count
        const newTotalEncForThisItem =
          Math.round((newTotalEncForThisItemRaw + Number.EPSILON) * 100) / 100

        // incremental ENC change for this item
        const deltaEncRaw = newTotalEncForThisItem - prevEncForThisItem
        const deltaEnc = Math.round((deltaEncRaw + Number.EPSILON) * 100) / 100

        console.log(
          'CounterDialog: prevEncForThisItem = ',
          prevEncForThisItem,
          ' newTotalEncForThisItem = ',
          newTotalEncForThisItem,
          ' deltaEnc = ',
          deltaEnc,
        )

        // if ENC is being reduced or unchanged, no capacity issue anywhere
        if (deltaEnc <= 0) {
          return true
        }

        // 1) check immediate container capacity using contents_enc + delta
        const newContentsEncRaw = contentsEnc + deltaEnc
        const newContentsEnc =
          Math.round((newContentsEncRaw + Number.EPSILON) * 100) / 100
        const availableBeforeRaw = capacity - contentsEnc
        const availableBefore =
          Math.round((availableBeforeRaw + Number.EPSILON) * 100) / 100

        console.log(
          'CounterDialog: immediate container check: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' deltaEnc = ',
          deltaEnc,
          ' newContentsEnc = ',
          newContentsEnc,
        )

        if (newContentsEnc - capacity > 0) {
          this.errors = []

          this.errors.push(
            window.bbUiErrorMsg(
              `Cannot change tally qty to ${count}: ${container.name} available enc capacity (${this.formatEnc(
                availableBefore,
              )}).\nRequired extra (${this.formatEnc(deltaEnc)})`,
            ),
          )

          availability = false
        }

        // 2) if immediate container ok, ensure all parent containers up
        //    the chain also have enough spare capacity for deltaEnc
        if (availability) {
          const parentChain = this.getParentContainerChain(this.row.container)
          for (const parent of parentChain) {
            const parentCapacity = parent.capacity
            const parentContentsEnc = parent.contents_enc
            const parentNewContentsRaw = parentContentsEnc + deltaEnc
            const parentNewContents =
              Math.round((parentNewContentsRaw + Number.EPSILON) * 100) / 100
            const parentAvailableBeforeRaw = parentCapacity - parentContentsEnc
            const parentAvailableBefore =
              Math.round((parentAvailableBeforeRaw + Number.EPSILON) * 100) /
              100

            console.log(
              'CounterDialog: parent container check: ',
              parent.name,
              ' capacity = ',
              parentCapacity,
              ' contentsEnc = ',
              parentContentsEnc,
              ' deltaEnc = ',
              deltaEnc,
              ' newContentsEnc = ',
              parentNewContents,
            )

            if (parentNewContents - parentCapacity > 0) {
              this.errors = []

              // build container chain string from the immediate container
              // up to the parent that exceeds capacity
              const immediateName = container.name || 'Container'
              const parentIndex = parentChain.findIndex(
                (p) => p.id === parent.id,
              )
              const chainUpToParent =
                parentIndex === -1
                  ? parentChain
                  : parentChain.slice(0, parentIndex + 1)
              const chainNames = [
                immediateName,
                ...chainUpToParent.map((p) => p.name).filter(Boolean),
              ]
              const chainLabel = chainNames.join(' -> ')

              this.errors.push(
                window.bbUiErrorMsg(
                  `Cannot change tally qty to ${count}: Container chain: ${chainLabel}.\nAvailable enc capacity of parent container (${this.formatEnc(
                    parentAvailableBefore,
                  )}). Required (${this.formatEnc(deltaEnc)})`,
                ),
              )

              availability = false
              break
            }
          }
        }
      }
      return availability
    },
    //========================================================
    // get parent container chain (immediate -> top)
    //-----------------------------------------------
    getParentContainerChain(containerId) {
      const chain = []
      if (
        !containerId ||
        !this.character ||
        !Array.isArray(this.character.items)
      ) {
        return chain
      }

      let current =
        this.character.items.find((c) => c.id === containerId) || null
      while (current && current.container) {
        const parentId = current.container
        const parent =
          this.character.items.find((c) => c.id === parentId) || null
        if (
          !parent ||
          !parent.container_size ||
          typeof parent.container_size.capacity !== 'number'
        ) {
          break
        }

        const capacity = parent.container_size.capacity
        const contentsEnc = parent.contents_enc ?? 0
        const rawAvailable = capacity - contentsEnc
        const availability =
          Math.round((rawAvailable + Number.EPSILON) * 100) / 100

        chain.push({
          id: parent.id,
          name: parent.display_name || parent.name || '',
          capacity,
          contents_enc: contentsEnc,
          availability,
        })

        current = parent
      }

      return chain
    },
    //========================================================
    // generic ENC formatter (two decimals)
    //-------------------------------------
    formatEnc(value) {
      const n = Number(value)
      if (Number.isNaN(n)) return ''
      if (n === 0) return '0.00'
      if (this.global && typeof this.global.twoDecimal === 'function') {
        return this.global.twoDecimal(n)
      }
      return n.toFixed(2)
    },
    //=====================================================================
    // confirm
    //---------
    _confirm() {
      sleep(100).then(() => {
        this.$refs.popup.close()
        this.resolvePromise(this.value)
      })
    },
    //=====================================================================
    // cancel
    //--------
    _cancel() {
      this.$refs.popup.close()
      this.resolvePromise()
    },
  },
}
</script>

<style scoped>
/* reduce counter label font-size only inside this dialog */
::v-deep(.text-center.col-auto .bb-float-label) {
  font-size: 0.8em;
}
</style>
